import { useEffect, useState, type ReactNode } from 'react';

/**
 * États communs de 59 §3.2 : chargement, vide, erreur, accès refusé, hors ligne. Un état « accès
 * refusé » **informe** ; il ne décide rien : c'est la réponse du serveur qui le déclenche.
 */

export function Loading({ label = 'Chargement…' }: { label?: string }) {
  return (
    <div className="ay-state ay-state--loading" role="status" aria-live="polite">
      <span className="ay-spinner" aria-hidden="true" />
      {label}
    </div>
  );
}

export function Empty({ label = 'Aucun élément.', title, children }: { label?: string; title?: string; children?: ReactNode }) {
  return (
    <div className="ay-state ay-state--empty" role="status">
      {title !== undefined && <strong className="ay-state__title">{title}</strong>}
      <p>{label}</p>
      {children}
    </div>
  );
}

/** Squelette de lignes (design system, `list-states`) : la forme de la liste avant ses données. */
export function ListSkeleton({ rows = 6, label = 'Chargement de la liste…' }: { rows?: number; label?: string }) {
  return (
    <div className="ay-skeleton-list" role="status" aria-live="polite" aria-busy="true">
      <span className="ay-visually-hidden">{label}</span>
      {Array.from({ length: rows }, (_, index) => (
        <div key={index} className="ay-skeleton" style={{ width: `${[60, 100, 80, 92, 70, 85][index % 6]}%` }} aria-hidden="true" />
      ))}
    </div>
  );
}

export function ErrorState({ message, code, onRetry }: { message: string; code?: string; onRetry?: () => void }) {
  return (
    <div className="ay-state ay-state--error" role="alert">
      <p>{message}</p>
      {code !== undefined && <p className="ay-state__code">{code}</p>}
      {onRetry !== undefined && (
        <button type="button" className="ay-button" onClick={onRetry}>
          Réessayer
        </button>
      )}
    </div>
  );
}

export function PermissionDenied({ message = 'Vous n’avez pas accès à cette ressource.' }: { message?: string }) {
  return (
    <div className="ay-state ay-state--denied" role="alert">
      <p>{message}</p>
    </div>
  );
}

/** Suit la connectivité du navigateur (hors ligne / dégradé, 59 §3.2). */
export function useOnline(): boolean {
  const [online, setOnline] = useState(() => (typeof navigator === 'undefined' ? true : navigator.onLine));
  useEffect(() => {
    const up = (): void => setOnline(true);
    const down = (): void => setOnline(false);
    window.addEventListener('online', up);
    window.addEventListener('offline', down);
    return () => {
      window.removeEventListener('online', up);
      window.removeEventListener('offline', down);
    };
  }, []);
  return online;
}

export function OfflineBanner() {
  const online = useOnline();
  if (online) return null;
  return (
    <div className="ay-banner ay-banner--warning" role="status">
      Hors ligne : les données affichées peuvent être anciennes ; aucune action n’est envoyée.
    </div>
  );
}

/** Erreur de l'API → état adapté. */
export function errorView(error: { category?: string; code?: string; message: string }, onRetry?: () => void): ReactNode {
  if (error.category === 'AUTHORIZATION_ERROR' || error.category === 'POLICY_REJECTION') return <PermissionDenied />;
  if (error.category === 'NOT_FOUND') return <Empty label="Introuvable." />;
  const props: { message: string; code?: string; onRetry?: () => void } = { message: error.message };
  if (error.code !== undefined) props.code = error.code;
  if (onRetry !== undefined) props.onRetry = onRetry;
  return <ErrorState {...props} />;
}
